import { Button } from '@mui/material';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuidv4 } from 'uuid';

import { ExtensionConfig, ModuleSettingsItem } from 'src/constants';
import { RouteContext, useFeatureFlags } from 'src/context';
import {
  AppLimitGate,
  isUsageAtLimit,
  useAppLimit,
} from 'src/features/appBuilder/app-limit-modal';
import history from 'src/history';
import { useAppDispatch } from 'src/hooks/useStore';
import { insertModuleSettingsItem } from 'src/legacy/components/Settings/AppSetupPage/folderUtils';
import { useAppSetupPaths } from 'src/legacy/components/Settings/AppSetupPage/useAppSetupPaths';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { ApiTags } from 'src/services/api/appApi';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { studioCreditApi } from 'src/services/api/studioCreditApi';
import {
  updateExtensionSettingsAction,
  updateModuleSettings,
} from 'src/store/settings/actions';
import { GraySmall } from 'src/theme/colors';

const useStyles = makeStyles()((theme) => ({
  description: {
    color: GraySmall,
    paddingTop: theme.spacing(0.5),
  },
  button: {
    marginTop: theme.spacing(3),
  },
}));

interface DuplicateQuery {
  moduleId?: string;
  appId?: string;
  preset?: string;
}

interface DuplicateAppInstanceProps {
  // Props take precedence over the URL query; the legacy page omits both and
  // lets the query supply them, while the app-builder settings pane passes
  // them explicitly because the builder URL carries neither.
  moduleId?: string;
  appId?: string;
}

export const DuplicateAppInstance = ({
  moduleId: propModuleId,
  appId: propAppId,
}: DuplicateAppInstanceProps = {}) => {
  const { EnableMultiInstanceApps } = useFeatureFlags();
  const { query } = React.useContext(RouteContext);
  const {
    moduleId: queryModuleId,
    appId: queryAppId,
    preset,
  } = query as DuplicateQuery;
  const moduleId = propModuleId ?? queryModuleId;
  const appId = propAppId ?? queryAppId;
  const { getSetupAppPagePath, folderId } = useAppSetupPaths();
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const [isDuplicating, setIsDuplicating] = React.useState(false);
  // Whether the app-limit paywall is up: the duplicate is held until the user
  // picks the fee or an upgrade.
  const [isGateOpen, setIsGateOpen] = React.useState(false);
  const { studioPlan, refetchUsage } = useAppLimit();

  const { data: { moduleSettings = [], extensionsSettings = {} } = {} } =
    useGetInstallsQuery();

  const isAddMode = !moduleId || moduleId === 'new';
  const sourceModule = isAddMode
    ? undefined
    : moduleSettings.find((m) => m.id === moduleId);
  const sourceExtension =
    isAddMode || !moduleId ? undefined : extensionsSettings[moduleId];

  if (!EnableMultiInstanceApps) return null;
  // Only custom apps and marketplace apps have an appId in query + a matching
  // extensionsSettings entry. Core modules, embeds, links, and folders don't.
  if (!sourceModule || !sourceExtension?.appId || !appId) return null;

  const performDuplicate = async () => {
    if (isDuplicating) return;
    setIsGateOpen(false);
    setIsDuplicating(true);
    const newModuleId = uuidv4();
    const copyLabel = sourceModule.label
      ? `${sourceModule.label} (copy)`
      : '(copy)';

    const newExtension: ExtensionConfig = {
      ...sourceExtension,
      id: newModuleId,
      name: copyLabel,
      embedCount: 0,
      // Duplicates are always non-root variants of the source's AppID group;
      // the source remains the root regardless of its own flag.
      isRoot: false,
    };

    const newModuleSettingsItem: ModuleSettingsItem = {
      id: newModuleId,
      label: copyLabel,
      icon: sourceModule.icon,
      type: sourceModule.type,
      disabled: false,
      visibilityConfig: sourceModule.visibilityConfig,
      iuSidebarHidden: sourceModule.iuSidebarHidden,
      isInternalApp: sourceModule.isInternalApp,
    };

    // Preserve folder placement: put the new instance in the same folder as
    // the source, falling back to the current URL's folderId.
    const targetFolderId = sourceModule.path ?? folderId;

    try {
      await dispatch(
        updateExtensionSettingsAction({
          ...extensionsSettings,
          [newModuleId]: newExtension,
        }),
      );

      try {
        await dispatch(
          updateModuleSettings(
            insertModuleSettingsItem(
              moduleSettings,
              newModuleSettingsItem,
              targetFolderId,
            ),
            { disableSuccessAlert: true },
          ),
        );
      } catch (moduleErr) {
        // Roll back the extension settings entry if the module settings update fails
        // to avoid an orphaned extension with no corresponding sidebar item.
        await dispatch(updateExtensionSettingsAction(extensionsSettings));
        throw moduleErr;
      }

      // The app count changed, so the cached usage is stale — invalidate it
      // so other surfaces gate against the fresh count.
      dispatch(studioCreditApi.util.invalidateTags([ApiTags.studioAppUsage]));

      history.push(
        getSetupAppPagePath({
          moduleType: 'extension',
          moduleId: newModuleId,
          preset: preset ?? 'custom',
          appId,
        }),
      );
    } finally {
      setIsDuplicating(false);
    }
  };

  // A duplicate is always an appId-backed extension (the component renders
  // nothing otherwise), so it spends an allowance slot unless the catalog
  // excludes its app. Re-read the authoritative count rather than trusting a
  // cached value; the server enforces the cap either way, so an unresolved
  // read simply proceeds.
  const handleClick = async () => {
    if (isDuplicating) return;
    setIsDuplicating(true);
    const usage = await refetchUsage();
    setIsDuplicating(false);

    if (isUsageAtLimit(usage, sourceExtension.appId) && studioPlan) {
      setIsGateOpen(true);
      return;
    }
    await performDuplicate();
  };

  return (
    <div>
      <BaseTypography fontType="18Medium">
        Install another instance
      </BaseTypography>
      <BaseTypography fontType="13Regular" className={classes.description}>
        Create another copy of this app with the same configuration. You can
        edit the new instance separately.
      </BaseTypography>
      <Button
        onClick={() => {
          void handleClick();
        }}
        variant="contained"
        color="primary"
        disabled={isDuplicating}
        className={classes.button}
      >
        Install another instance
      </Button>
      {/* Only with a resolved Studio plan: the flag gates the whole allowance
          surface, and the upgrade route needs a plan to pitch from. */}
      {isGateOpen && studioPlan && (
        <AppLimitGate
          studioPlan={studioPlan}
          surface="add"
          app={{
            name: sourceModule.label,
            icon: { source: 'icon', name: 'cube' },
          }}
          onClose={() => setIsGateOpen(false)}
          onProceed={() => {
            void performDuplicate();
          }}
        />
      )}
    </div>
  );
};
